import React, { useState, ChangeEvent, useEffect, useRef } from "react";
import { Callout } from "@fluentui/react";
import { Button } from "@fluentui/react-components";
import { useMsal } from "@azure/msal-react";
import { useTranslation } from "react-i18next";

import { uploadFileApi, deleteUploadedFileApi, listUploadedFilesApi } from "../../api";
import { SessionAttachment } from "../../types/uploads";
import { useMsalAuth, getToken } from "../../authConfig";
import styles from "./UploadFile.module.css";
import { Upload, FileText, X, FolderOpen, Plus, Paperclip } from "lucide-react";

interface DocumentFile {
    name: string;
    uploadedAt?: Date;
}

interface Props {
    className?: string;
    disabled?: boolean;
    onFileUploaded?: (file: SessionAttachment) => void;
    externalFiles?: File[];
    onExternalFilesProcessed?: () => void;
    onUploadStart?: (filename: string) => void;
    onUploadEnd?: () => void;
    onDocumentSearchToggle?: (enabled: boolean) => void;
    onDocumentFilesChange?: (files: string[]) => void;
    useDocumentSearch?: boolean;
}

export const UploadFile: React.FC<Props> = ({
    className,
    disabled,
    onFileUploaded,
    externalFiles,
    onExternalFilesProcessed,
    onUploadStart,
    onUploadEnd,
    onDocumentSearchToggle,
    onDocumentFilesChange,
    useDocumentSearch = false
}: Props) => {
    const ALLOWED_EXTENSIONS = [".txt", ".md", ".json", ".csv", ".png", ".jpg", ".jpeg", ".bmp", ".heic", ".tiff", ".pdf", ".docx", ".xlsx", ".pptx", ".html"];

    // State variables to manage the component behavior
    const [isCalloutVisible, setIsCalloutVisible] = useState<boolean>(false);
    const [isSessionUploading, setIsSessionUploading] = useState<boolean>(false);
    const [isDatabaseUploading, setIsDatabaseUploading] = useState<boolean>(false);
    const [uploadedFileError, setUploadedFileError] = useState<string>();
    const [isDragging, setIsDragging] = useState<boolean>(false);
    const [documents, setDocuments] = useState<DocumentFile[]>([]);
    const [isLoadingDocs, setIsLoadingDocs] = useState<boolean>(false);
    const [activeTab, setActiveTab] = useState<"upload" | "documents">("upload");
    const [isDocumentsExpanded, setIsDocumentsExpanded] = useState<boolean>(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const databaseInputRef = useRef<HTMLInputElement>(null);
    const { t } = useTranslation();

    // Support both MSAL (old Azure AD users) and custom OAuth (email/Google/Microsoft via custom auth)
    const msalEnabled = useMsalAuth;
    const msalContext = msalEnabled ? useMsal() : null;
    const client = msalContext?.instance;
    type UploadDestination = "session" | "database";

    // Helper to get auth token - works with both MSAL and custom OAuth (session cookies)
    const getAuthToken = async (): Promise<string | undefined> => {
        if (msalEnabled && client) {
            // Old Azure AD users - get MSAL token
            return await getToken(client);
        }
        // Custom OAuth users (email/Google/Microsoft) - session cookies are sent automatically
        // No token needed, backend validates session cookie
        return undefined;
    };

    // Load documents when component mounts
    useEffect(() => {
        loadDocuments();
    }, []);

    // Also reload documents when callout opens
    useEffect(() => {
        if (isCalloutVisible && activeTab === "documents") {
            loadDocuments();
        }
    }, [isCalloutVisible, activeTab]);

    // Auto-expand documents section if there are files
    useEffect(() => {
        if (documents.length > 0) {
            setIsDocumentsExpanded(true);
        }
    }, [documents]);

    const loadDocuments = async () => {
        setIsLoadingDocs(true);
        try {
            const token = await getAuthToken();
            const files = await listUploadedFilesApi(token);
            const mappedFiles = files.map(name => ({ name }));
            setDocuments(mappedFiles);
            onDocumentFilesChange?.(mappedFiles.map(doc => doc.name));
        } catch (error) {
            console.error("Failed to load documents:", error);
        } finally {
            setIsLoadingDocs(false);
        }
    };

    const handleDeleteDocument = async (filename: string) => {
        try {
            const token = await getAuthToken();
            await deleteUploadedFileApi(filename, token);
            setDocuments(prev => {
                const next = prev.filter(doc => doc.name !== filename);
                onDocumentFilesChange?.(next.map(doc => doc.name));
                return next;
            });
        } catch (error) {
            console.error("Failed to delete document:", error);
        }
    };

    // Helper function to upload files either to the current chat session or the document database
    const uploadFile = async (file: File, destination: UploadDestination) => {
        const fileExtension = file.name.substring(file.name.lastIndexOf(".")).toLowerCase();

        if (!ALLOWED_EXTENSIONS.includes(fileExtension)) {
            setUploadedFileError(t("upload.invalidFileType") || `Invalid file type. Allowed: ${ALLOWED_EXTENSIONS.join(", ")}`);
            return false;
        }

        const notifySessionCallbacks = destination === "session";
        if (notifySessionCallbacks) {
            onUploadStart?.(file.name);
        }

        const formData = new FormData();
        formData.append("file", file);
        formData.append("destination", destination);

        try {
            const idToken = await getAuthToken();
            const response = await uploadFileApi(formData, idToken);
            setUploadedFileError(undefined);

            if (destination === "session") {
                const payload = response.session_file;
                if (!payload) {
                    throw new Error("Session upload failed: missing payload");
                }
                const attachment: SessionAttachment = {
                    id: payload.id,
                    name: payload.name,
                    contentType: payload.content_type,
                    size: payload.size,
                    text: payload.text || "",
                    truncated: payload.truncated ?? false,
                    dataUrl: payload.data_url,
                    sasUrl: payload.sas_url,
                    blobUrl: payload.blob_url,
                    blobPath: payload.blob_path
                };
                onFileUploaded?.(attachment);
            } else {
                setDocuments(prev => {
                    const next = [...prev, { name: file.name, uploadedAt: new Date() }];
                    onDocumentFilesChange?.(next.map(doc => doc.name));
                    return next;
                });
            }
            return true;
        } catch (error) {
            console.error(error);
            setUploadedFileError(t("upload.uploadedFileError") || "Failed to upload file");
            return false;
        } finally {
            if (notifySessionCallbacks) {
                onUploadEnd?.();
            }
        }
    };

    const uploadFiles = async (filesToUpload: FileList | File[], destination: UploadDestination) => {
        const fileArray = Array.isArray(filesToUpload) ? filesToUpload : Array.from(filesToUpload);
        if (fileArray.length === 0) {
            return;
        }

        setUploadedFileError(undefined);
        if (destination === "session") {
            setIsSessionUploading(true);
        } else {
            setIsDatabaseUploading(true);
        }

        let successfulUploads = 0;
        let failedUploads = 0;

        try {
            for (const file of fileArray) {
                const success = await uploadFile(file, destination);
                if (success) {
                    successfulUploads += 1;
                } else {
                    failedUploads += 1;
                }
            }
        } finally {
            if (destination === "session") {
                setIsSessionUploading(false);
            } else {
                setIsDatabaseUploading(false);
            }
        }

        if (destination === "session" && successfulUploads > 0 && failedUploads === 0) {
            setIsCalloutVisible(false);
        } else if (destination === "database" && successfulUploads > 0) {
            // Refresh documents to ensure consistency with backend metadata
            loadDocuments();
        }
    };

    // Handle external files dropped on the chat input
    useEffect(() => {
        if (externalFiles && externalFiles.length > 0) {
            uploadFiles(externalFiles, "session").finally(() => {
                // Call the callback to clear the external files
                onExternalFilesProcessed?.();
            });
        }
    }, [externalFiles]);

    const handleButtonClick = () => {
        setIsCalloutVisible(!isCalloutVisible);
        setUploadedFileError(undefined);
        // Keep the current tab when opening/closing the callout
    };

    // Handler for file upload
    const handleUploadFile = async (e: ChangeEvent<HTMLInputElement>) => {
        e.preventDefault();
        const files = e.target.files;
        if (!files || files.length === 0) {
            return;
        }

        await uploadFiles(files, "session");

        // Reset the input
        e.target.value = "";
    };

    // Drag and drop handlers
    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
        // Auto-open callout when dragging over
        if (!isCalloutVisible) {
            setIsCalloutVisible(true);
        }
    };

    const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        // Only set dragging to false if we're leaving the callout entirely
        const rect = e.currentTarget.getBoundingClientRect();
        const x = e.clientX;
        const y = e.clientY;
        if (x < rect.left || x >= rect.right || y < rect.top || y >= rect.bottom) {
            setIsDragging(false);
        }
    };

    const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        const files = e.dataTransfer.files;
        if (files && files.length > 0) {
            await uploadFiles(files, "session");
        }
    };

    const handleDocumentDatabaseClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        databaseInputRef.current?.click();
    };

    const handleDocumentDatabaseChange = (event: ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (!files || files.length === 0) {
            return;
        }

        void uploadFiles(files, "database");
        event.target.value = "";
    };

    const filteredDocuments = documents;

    const handleUploadClick = () => {
        fileInputRef.current?.click();
    };

    return (
        <div
            className={`${styles.container} ${className ?? ""}`}
            onClick={handleButtonClick}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
        >
            <div>
                <Button id="calloutButton" disabled={disabled}>
                    <Paperclip className={styles.documentButtonIcon} size={18} style={{ width: "16px", height: "18px", flexShrink: 0 }} />
                    <span style={{ marginLeft: "3px", fontSize: "var(--font-sm)" }}>{t("upload.buttonLabel", "Upload")}</span>
                </Button>

                {isCalloutVisible && (
                    <Callout
                        role="dialog"
                        gapSpace={0}
                        className={`${styles.callout} ${isDragging ? styles.dragging : ""}`}
                        target="#calloutButton"
                        onDismiss={() => setIsCalloutVisible(false)}
                        setInitialFocus
                        styles={{
                            beak: { display: "none" },
                            beakCurtain: { borderRadius: "16px" }
                        }}
                    >
                        <div className={styles.documentsDropdown}>
                            {/* Header with Documents text */}
                            <div className={styles.dropdownHeader}>
                                <span className={styles.headerTitle}>{t("documents") || "Documents"}</span>
                            </div>

                            {/* Upload Section */}
                            <div
                                className={`${styles.uploadSection} ${isDragging ? styles.draggingActive : ""}`}
                                onClick={handleUploadClick}
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                                onDrop={handleDrop}
                            >
                                <div className={styles.uploadButtonNew}>
                                    <Upload size={16} />
                                    <span>{isDragging ? t("dropFiles") || "Drop files here" : t("uploadFiles") || "Upload files"}</span>
                                </div>
                                <input
                                    ref={fileInputRef}
                                    accept={ALLOWED_EXTENSIONS.join(",")}
                                    type="file"
                                    multiple
                                    onChange={handleUploadFile}
                                    className={styles.hiddenInput}
                                    disabled={isSessionUploading || isDatabaseUploading}
                                />
                            </div>

                            {/* Divider */}
                            <div className={styles.divider} />

                            {/* Document Search Toggle */}
                            <div className={styles.toggleSection}>
                                <div className={styles.toggleWrapper}>
                                    <span className={styles.toggleLabel}>{t("searchFromDocuments") || "Search from document"}</span>
                                    <label className={styles.toggleSwitch}>
                                        <input
                                            type="checkbox"
                                            checked={useDocumentSearch}
                                            onChange={e => {
                                                const isChecked = e.target.checked;
                                                onDocumentSearchToggle?.(isChecked);
                                                // Ensure documents are passed when toggle is turned on
                                                if (isChecked && documents.length > 0) {
                                                    onDocumentFilesChange?.(documents.map(doc => doc.name));
                                                }
                                            }}
                                        />
                                        <span className={styles.toggleSlider} />
                                    </label>
                                </div>
                            </div>

                            {/* Divider */}
                            <div className={styles.divider} />

                            {/* Documents List */}
                            <div className={styles.documentsSection}>
                                <div className={styles.sectionHeader}>
                                    <div
                                        className={`${styles.sectionHeaderMain} ${styles.clickableHeader}`}
                                        onClick={() => setIsDocumentsExpanded(!isDocumentsExpanded)}
                                    >
                                        <svg
                                            width="14"
                                            height="14"
                                            viewBox="0 0 16 16"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                            className={`${styles.chevron} ${isDocumentsExpanded ? styles.chevronExpanded : ""}`}
                                        >
                                            <path d="M10.85 8L5.84996 13L5.14996 12.3L9.44996 8L5.14996 3.7L5.84996 3L10.85 8Z" fill="currentColor" />
                                        </svg>
                                        <FolderOpen size={16} />
                                        <span>{t("documentDatabase") || "Document Database"}</span>
                                        <span className={styles.documentCount}>({filteredDocuments.length})</span>
                                    </div>
                                    <div className={styles.headerActions}>
                                        <button
                                            type="button"
                                            className={styles.addButton}
                                            onClick={handleDocumentDatabaseClick}
                                            disabled={isDatabaseUploading}
                                            aria-label={t("addToDocumentDatabase") || "Add to Document Database"}
                                        >
                                            <Plus size={16} />
                                        </button>
                                        <input
                                            ref={databaseInputRef}
                                            type="file"
                                            multiple
                                            accept={ALLOWED_EXTENSIONS.join(",")}
                                            onChange={handleDocumentDatabaseChange}
                                            className={styles.hiddenInput}
                                        />
                                    </div>
                                </div>

                                {isDocumentsExpanded && (
                                    <div className={styles.documentsList}>
                                        {isLoadingDocs ? (
                                            <div className={styles.loadingState}>
                                                <div className={styles.spinner} />
                                                <span>{t("loadingDocuments") || "Loading documents..."}</span>
                                            </div>
                                        ) : isDatabaseUploading ? (
                                            <div className={styles.uploadingState}>
                                                <div className={styles.spinner} />
                                                <span>{t("uploadingFiles") || "Uploading files..."}</span>
                                            </div>
                                        ) : filteredDocuments.length === 0 ? (
                                            <div className={styles.emptyState}>
                                                <FileText size={24} />
                                                <span>{t("noDocuments") || "No documents uploaded yet"}</span>
                                            </div>
                                        ) : (
                                            filteredDocuments.map(doc => (
                                                <div key={doc.name} className={styles.documentItem}>
                                                    <FileText size={16} className={styles.documentIcon} />
                                                    <span className={styles.documentName}>{doc.name}</span>
                                                    <button
                                                        className={styles.deleteButton}
                                                        onClick={e => {
                                                            e.stopPropagation();
                                                            handleDeleteDocument(doc.name);
                                                        }}
                                                        aria-label={t("deleteDocumentAria", { name: doc.name }) || `Delete ${doc.name}`}
                                                    >
                                                        <X size={14} />
                                                    </button>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Status messages */}
                            {uploadedFileError && <div className={styles.errorMessage}>{uploadedFileError}</div>}
                        </div>
                    </Callout>
                )}
            </div>
        </div>
    );
};
